/**
 * 认证状态管理器
 * 提供非阻塞的认证初始化和状态管理
 */

import { ref, computed } from 'vue'
import { tokenManager } from './tokenManager'
import apiClient from '@/services/api'
import type { User } from '@/stores/auth'

export interface AuthError {
  type: 'NETWORK_ERROR' | 'TOKEN_EXPIRED' | 'INVALID_TOKEN' | 'SERVER_ERROR' | 'TIMEOUT_ERROR'
  message: string
  code?: string
  retryable: boolean
  originalError?: any
}

export interface AuthState {
  isAuthenticated: boolean
  isInitializing: boolean
  user: User | null
  token: string | null
  lastError: AuthError | null
  retryCount: number
  lastAuthCheck: number
  initializationPromise: Promise<void> | null
}

export class AuthStateManager {
  private readonly AUTH_CHECK_TIMEOUT = 10000 // 10秒超时
  private readonly MAX_RETRY_COUNT = 3
  private readonly RETRY_DELAY_BASE = 1000 // 1秒基础延迟

  // 响应式状态
  private _state = ref<AuthState>({
    isAuthenticated: this._getInitialAuthState(),
    isInitializing: false,
    user: this._getInitialUserState(),
    token: tokenManager.getToken(),
    lastError: null,
    retryCount: 0,
    lastAuthCheck: 0,
    initializationPromise: null
  })

  /**
   * 获取初始认证状态
   * 如果有有效的本地令牌，初始状态为已认证
   */
  private _getInitialAuthState(): boolean {
    const token = tokenManager.getToken()
    if (!token) return false
    
    // 快速检查令牌是否有效
    return tokenManager.isTokenValidLocally()
  }

  /**
   * 获取初始用户状态
   * 尝试从 localStorage 恢复用户数据
   */
  private _getInitialUserState(): User | null {
    try {
      const storedUser = localStorage.getItem('auth_user')
      if (storedUser) {
        const user = JSON.parse(storedUser)
        console.log('[AuthStateManager] 从 localStorage 恢复初始用户数据:', user.email)
        return user
      }
    } catch (error) {
      console.warn('[AuthStateManager] 无法从 localStorage 恢复初始用户数据:', error)
    }
    return null
  }

  // 计算属性
  public readonly isAuthenticated = computed(() => this._state.value.isAuthenticated)
  public readonly isInitializing = computed(() => this._state.value.isInitializing)
  public readonly user = computed(() => this._state.value.user)
  public readonly token = computed(() => this._state.value.token)
  public readonly lastError = computed(() => this._state.value.lastError)

  /**
   * 非阻塞式认证初始化
   * 如果已经在初始化中，返回现有的 Promise
   */
  async initializeAuth(): Promise<void> {
    // 如果已经在初始化中，返回现有的 Promise
    if (this._state.value.initializationPromise) {
      return this._state.value.initializationPromise
    }

    // 创建新的初始化 Promise
    const initPromise = this._performAuthInitialization()
    this._state.value.initializationPromise = initPromise

    try {
      await initPromise
    } finally {
      // 清除初始化 Promise
      this._state.value.initializationPromise = null
    }
  }

  /**
   * 执行实际的认证初始化
   */
  private async _performAuthInitialization(): Promise<void> {
    console.log('[AuthStateManager] 开始认证初始化')
    
    this._state.value.isInitializing = true
    this._state.value.lastError = null

    try {
      // 1. 快速本地令牌检查
      if (!this._checkLocalToken()) {
        console.log('[AuthStateManager] 本地令牌检查失败，设置为未认证状态')
        this._setUnauthenticatedState()
        return
      }

      // 2. 网络验证令牌（带超时）
      await this._verifyTokenWithTimeout()

    } catch (error) {
      console.error('[AuthStateManager] 认证初始化失败:', error)
      this._handleAuthError(error)
    } finally {
      this._state.value.isInitializing = false
      this._state.value.lastAuthCheck = Date.now()
    }
  }

  /**
   * 检查本地令牌有效性
   */
  private _checkLocalToken(): boolean {
    const token = tokenManager.getToken()
    
    if (!token) {
      console.log('[AuthStateManager] 没有找到本地令牌')
      return false
    }

    if (!tokenManager.isTokenValidLocally()) {
      console.log('[AuthStateManager] 本地令牌验证失败')
      return false
    }

    // 更新状态中的令牌
    this._state.value.token = token
    
    // 更新 API 客户端配置
    apiClient.updateConfig({
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    console.log('[AuthStateManager] 本地令牌验证通过')
    return true
  }

  /**
   * 带超时的令牌验证
   */
  private async _verifyTokenWithTimeout(): Promise<void> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(this._createAuthError('TIMEOUT_ERROR', '认证检查超时', true))
      }, this.AUTH_CHECK_TIMEOUT)
    })

    const verifyPromise = this._verifyTokenWithServer()

    try {
      await Promise.race([verifyPromise, timeoutPromise])
    } catch (error) {
      // 如果是超时错误，尝试优雅降级
      if (error && typeof error === 'object' && 'type' in error && error.type === 'TIMEOUT_ERROR') {
        console.warn('[AuthStateManager] 认证检查超时，降级到本地状态')
        // 如果本地令牌看起来有效，暂时保持认证状态
        if (tokenManager.isTokenValidLocally()) {
          this._setAuthenticatedState(null) // 没有用户信息，但保持认证状态
          return
        }
      }
      throw error
    }
  }

  /**
   * 与服务器验证令牌
   */
  private async _verifyTokenWithServer(): Promise<void> {
    try {
      // const token = tokenManager.getToken()
      

      
      console.log('[AuthStateManager] 向服务器验证令牌')
      
      const response = await apiClient.get<{ user: User }>('/auth/me')
      
      if (response.success && response.data.user) {
        console.log('[AuthStateManager] 服务器令牌验证成功，用户信息:', {
          id: response.data.user.id,
          email: response.data.user.email,
          questionnaire_completed: response.data.user.questionnaire_completed,
          carbon_footprint_score: response.data.user.carbon_footprint_score
        })
        this._setAuthenticatedState(response.data.user)
      } else {
        throw this._createAuthError('INVALID_TOKEN', '服务器返回无效响应', false)
      }
    } catch (error: any) {
      // 根据错误类型进行不同处理
      if (error.status === 401) {
        // 401 错误：令牌无效，清除认证状态
        // 注意：令牌刷新由 API 客户端的拦截器处理，这里不再重复处理
        console.log('[AuthStateManager] 收到 401 错误，令牌无效')
        throw this._createAuthError('INVALID_TOKEN', '令牌无效', false, error)
      } else if (error.status >= 500) {
        // 服务器错误：可重试
        throw this._createAuthError('SERVER_ERROR', '服务器错误', true, error)
      } else if (!navigator.onLine) {
        // 网络错误：可重试
        throw this._createAuthError('NETWORK_ERROR', '网络连接错误', true, error)
      } else {
        // 其他错误：不可重试
        throw this._createAuthError('INVALID_TOKEN', '令牌验证失败', false, error)
      }
    }
  }

  /**
   * 尝试刷新令牌
   */
  // private async _attemptTokenRefresh(): Promise<void> {
  //   try {
  //     console.log('[AuthStateManager] 尝试刷新令牌')
      
  //     const response = await apiClient.post<{
  //       user: User
  //       token: string
  //       token_type: string
  //       expires_in: number
  //     }>('/auth/refresh')
      
  //     if (response.success && response.data) {
  //       console.log('[AuthStateManager] 令牌刷新成功')
        
  //       // 更新令牌
  //       tokenManager.setToken(
  //         response.data.token,
  //         response.data.expires_in,
  //         undefined // 刷新令牌通常在响应中
  //       )
        
  //       // 更新 API 客户端配置
  //       apiClient.updateConfig({
  //         headers: {
  //           'Authorization': `Bearer ${response.data.token}`
  //         }
  //       })
        
  //       // 设置认证状态
  //       this._setAuthenticatedState(response.data.user)
  //     } else {
  //       throw this._createAuthError('TOKEN_EXPIRED', '令牌刷新失败', false)
  //     }
  //   } catch (error: any) {
  //     console.error('[AuthStateManager] 令牌刷新失败:', error)
  //     throw this._createAuthError('TOKEN_EXPIRED', '令牌已过期且无法刷新', false, error)
  //   }
  // }

  /**
   * 设置认证状态
   */
  private _setAuthenticatedState(user: User | null): void {
    this._state.value.isAuthenticated = true
    this._state.value.token = tokenManager.getToken()
    this._state.value.lastError = null
    this._state.value.retryCount = 0
    
    // 如果提供了用户数据，使用它；否则尝试从 localStorage 恢复
    if (user) {
      this._state.value.user = user
      // 检查并恢复问卷完成状态
      if (user.id) {
        const localCompletedStatus = localStorage.getItem(`questionnaire_completed_${user.id}`)
        const localCompletedAt = localStorage.getItem(`questionnaire_completed_at_${user.id}`)
        
        if (localCompletedStatus === 'true' && !user.questionnaire_completed) {
          user.questionnaire_completed = true
          if (localCompletedAt) {
            user.questionnaire_completed_at = localCompletedAt
          }
          console.log('[AuthStateManager] 从 localStorage 恢复问卷完成状态')
        }
      }
    } else {
      // 尝试从 localStorage 恢复用户数据
      try {
        const storedUser = localStorage.getItem('auth_user')
        if (storedUser) {
          this._state.value.user = JSON.parse(storedUser)
          console.log('[AuthStateManager] 从 localStorage 恢复用户数据')
        }
      } catch (error) {
        console.warn('[AuthStateManager] 无法从 localStorage 恢复用户数据:', error)
        this._state.value.user = null
      }
    }
    
    console.log('[AuthStateManager] 设置为认证状态')
  }

  /**
   * 设置未认证状态
   */
  private _setUnauthenticatedState(clearError: boolean = true): void {
    this._state.value.isAuthenticated = false
    this._state.value.user = null
    this._state.value.token = null
    
    if (clearError) {
      this._state.value.lastError = null
      this._state.value.retryCount = 0
    }
    
    // 清除令牌和 API 配置
    tokenManager.removeToken()
    apiClient.updateConfig({
      headers: {
        'Authorization': ''
      }
    })
    
    console.log('[AuthStateManager] 设置为未认证状态')
  }

  /**
   * 处理认证错误
   */
  private _handleAuthError(error: any): void {
    let authError: AuthError

    if (error && typeof error === 'object' && 'type' in error) {
      // 已经是 AuthError
      authError = error as AuthError
    } else {
      // 转换为 AuthError
      authError = this._createAuthError('SERVER_ERROR', '未知认证错误', true, error)
    }

    this._state.value.lastError = authError

    // 根据错误类型决定是否重试
    if (authError.retryable && this._state.value.retryCount < this.MAX_RETRY_COUNT) {
      this._scheduleRetry()
    } else {
      // 不可重试或达到最大重试次数，设置为未认证状态但保留错误信息
      this._setUnauthenticatedState(false)
    }
  }

  /**
   * 安排重试
   */
  private _scheduleRetry(): void {
    this._state.value.retryCount++
    const delay = this.RETRY_DELAY_BASE * Math.pow(2, this._state.value.retryCount - 1)
    
    console.log(`[AuthStateManager] 安排第 ${this._state.value.retryCount} 次重试，延迟 ${delay}ms`)
    
    setTimeout(() => {
      this.initializeAuth()
    }, delay)
  }

  /**
   * 创建认证错误对象
   */
  private _createAuthError(
    type: AuthError['type'],
    message: string,
    retryable: boolean,
    originalError?: any
  ): AuthError {
    return {
      type,
      message,
      retryable,
      originalError,
      code: originalError?.status?.toString()
    }
  }

  /**
   * 清除认证状态
   */
  clearAuthState(): void {
    console.log('[AuthStateManager] 清除认证状态')
    this._setUnauthenticatedState()
  }

  /**
   * 设置认证数据（用于登录成功后）
   */
  setAuthData(user: User, token: string, expiresIn: number): void {
    console.log('[AuthStateManager] 设置认证数据')
    
    // 存储令牌
    tokenManager.setToken(token, expiresIn)
    
    // 更新 API 客户端配置
    apiClient.updateConfig({
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    // 设置认证状态
    this._setAuthenticatedState(user)
  }

  /**
   * 更新用户数据（保持认证状态不变）
   */
  updateUserData(user: User): void {
    console.log('[AuthStateManager] 更新用户数据')
    
    if (this._state.value.isAuthenticated) {
      this._state.value.user = user
      
      // 持久化用户数据到 localStorage
      try {
        localStorage.setItem('auth_user', JSON.stringify(user))
        console.log('[AuthStateManager] 用户数据已持久化到 localStorage')
      } catch (error) {
        console.warn('[AuthStateManager] 无法持久化用户数据到 localStorage:', error)
      }
    }
  }

  /**
   * 获取当前状态快照
   */
  getStateSnapshot(): AuthState {
    return { ...this._state.value }
  }

  /**
   * 获取调试信息
   */
  getDebugInfo(): {
    state: AuthState
    tokenSummary: ReturnType<typeof tokenManager.getTokenSummary>
  } {
    return {
      state: this.getStateSnapshot(),
      tokenSummary: tokenManager.getTokenSummary()
    }
  }
}

// 导出单例实例
export const authStateManager = new AuthStateManager()